import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, HelpCircle, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const faqs = [
    {
      category: "Design & Customization",
      questions: [
        {
          question: "How do I create a custom Kente design?",
          answer:
            "Use our designer tool to select from authentic Kente patterns, add personalized text, and choose from our collection of Ghanaian symbols. You can customize colors, positioning, and sizing to create your unique design.",
        },
        {
          question: "Can I use my own images or logos?",
          answer:
            "Currently, we support our curated collection of authentic Kente patterns and Ghanaian symbols. We're working on adding custom image upload functionality in the future.",
        },
        {
          question: "What text can I add to my design?",
          answer:
            "You can add any text you'd like, such as names, dates, quotes, or messages. We recommend keeping text concise for the best visual impact on your Kente sash.",
        },
      ],
    },
    {
      category: "Orders & Payment",
      questions: [
        {
          question: "How do I place an order?",
          answer:
            "After creating your design, click the 'Add to Cart' button, review your order, and proceed to checkout. You can pay using credit card, PayPal, or other accepted payment methods.",
        },
        {
          question: "What payment methods do you accept?",
          answer:
            "We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and bank transfers for larger orders.",
        },
        {
          question: "Can I modify my order after placing it?",
          answer:
            "You can modify your order within 2 hours of placing it. After that, changes may not be possible as production may have already begun.",
        },
      ],
    },
    {
      category: "Shipping & Delivery",
      questions: [
        {
          question: "How long does shipping take?",
          answer:
            "Standard shipping takes 7-14 business days. Express shipping (3-5 days) and overnight shipping are also available for an additional fee.",
        },
        {
          question: "Do you ship internationally?",
          answer:
            "Yes, we ship to select countries worldwide. International shipping times and costs vary by destination. Please check our shipping page for details.",
        },
        {
          question: "How can I track my order?",
          answer:
            "Once your order ships, you'll receive a tracking number via email. You can use this to track your package's progress on our website or the carrier's website.",
        },
      ],
    },
    {
      category: "Quality & Care",
      questions: [
        {
          question: "What materials are used in your Kente sashes?",
          answer:
            "Our Kente sashes are made from high-quality, authentic Kente cloth using traditional weaving techniques. We work with skilled Ghanaian artisans to ensure authentic craftsmanship.",
        },
        {
          question: "How should I care for my Kente sash?",
          answer:
            "Hand wash in cold water with mild detergent, lay flat to dry, and iron on low heat if needed. Avoid bleach and harsh chemicals to preserve the colors and fabric.",
        },
        {
          question: "What if I'm not satisfied with my order?",
          answer:
            "We offer a 30-day return policy for items in original condition. Custom designs can be returned if there's a manufacturing defect. Please contact us for return instructions.",
        },
      ],
    },
  ];

  const filteredFaqs = faqs
    .map((category) => ({
      ...category,
      questions: category.questions.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    }))
    .filter((category) => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
            <h1 className="text-xl font-bold">Frequently Asked Questions</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Search Section */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">
                Find Answers to Your Questions
              </h2>
              <div className="relative max-w-md mx-auto">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search FAQs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {filteredFaqs.map((category, categoryIndex) => (
            <Card key={categoryIndex}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5" />
                  {category.category}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {category.questions.map((faq, faqIndex) => (
                    <div
                      key={faqIndex}
                      className="border-b border-border pb-4 last:border-b-0"
                    >
                      <h3 className="font-semibold mb-2 text-lg">
                        {faq.question}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Contact Support */}
        {filteredFaqs.length === 0 && searchTerm && (
          <Card className="mt-8">
            <CardContent className="pt-6 text-center">
              <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No results found</h3>
              <p className="text-muted-foreground mb-4">
                We couldn't find any FAQs matching your search. Try different
                keywords or contact our support team.
              </p>
              <Button>Contact Support</Button>
            </CardContent>
          </Card>
        )}

        {/* Still Need Help */}
        <Card className="mt-8">
          <CardContent className="pt-6 text-center">
            <HelpCircle className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Still Need Help?</h3>
            <p className="text-muted-foreground mb-4">
              Can't find the answer you're looking for? Our support team is here
              to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button>Contact Support</Button>
              <Button variant="outline">Live Chat</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FAQ;
